import { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const UnifiedDashboardContext = createContext();

export const useUnifiedDashboard = () => {
  const context = useContext(UnifiedDashboardContext);
  if (!context) {
    throw new Error("useUnifiedDashboard must be used within a UnifiedDashboardProvider");
  }
  return context;
};

export const UnifiedDashboardProvider = ({ children }) => {
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [dashboardHistory, setDashboardHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [userPreferences, setUserPreferences] = useState({
    lastUsedDashboard: "qm",
    dashboardBookmarks: [],
    recentViews: [],
    theme: "light"
  });

  const { user, isAuthenticated } = useAuth();

  // Load user preferences on authentication
  useEffect(() => {
    if (isAuthenticated()) {
      const savedPreferences = localStorage.getItem(`pkf_dashboard_preferences_${user?.email}`);
      if (savedPreferences) {
        try {
          const preferences = JSON.parse(savedPreferences);
          setUserPreferences(prev => ({ ...prev, ...preferences }));
        } catch (error) {
          console.error("Error loading dashboard preferences:", error);
        }
      }
    }
  }, [isAuthenticated, user?.email]);

  // Save preferences when they change
  useEffect(() => {
    if (isAuthenticated() && user?.email) {
      localStorage.setItem(
        `pkf_dashboard_preferences_${user.email}`,
        JSON.stringify(userPreferences)
      );
    }
  }, [userPreferences, isAuthenticated, user?.email]);

  const switchDashboard = (dashboardType) => {
    if (currentDashboard !== dashboardType) {
      // Add to history
      if (currentDashboard) {
        setDashboardHistory(prev => {
          const newHistory = [currentDashboard, ...prev.filter(d => d !== currentDashboard)];
          return newHistory.slice(0, 5); // Keep only last 5
        });
      }

      setCurrentDashboard(dashboardType);
      
      // Update preferences
      setUserPreferences(prev => ({
        ...prev,
        lastUsedDashboard: dashboardType,
        recentViews: [
          { dashboard: dashboardType, timestamp: new Date().toISOString() },
          ...prev.recentViews.filter(v => v.dashboard !== dashboardType)
        ].slice(0, 10) // Keep only last 10
      }));
    }
  };

  const addBookmark = (dashboard, view, label) => {
    const bookmark = {
      id: `${dashboard}-${view}-${Date.now()}`,
      dashboard,
      view,
      label,
      timestamp: new Date().toISOString()
    };

    setUserPreferences(prev => ({
      ...prev,
      dashboardBookmarks: [bookmark, ...prev.dashboardBookmarks.slice(0, 19)] // Max 20 bookmarks
    }));
  };

  const removeBookmark = (bookmarkId) => {
    setUserPreferences(prev => ({
      ...prev,
      dashboardBookmarks: prev.dashboardBookmarks.filter(b => b.id !== bookmarkId)
    }));
  };

  const getDashboardConfig = (dashboardType) => {
    const configs = {
      qm: {
        name: "Quality Management",
        description: "Track late accounts, archival compliance, timesheets, and training hours",
        icon: "📊",
        color: "blue",
        features: [
          "Late Accounts Management",
          "Archival Compliance Tracking",
          "Archival Request & Approval"
        ]
      },
      ld: {
        name: "Professional Development",
        description: "Manage training sessions, track attendance, analyze quiz performance, and monitor professional qualifications",
        icon: "🎓",
        color: "green",
        features: [
          "Training Hours Comparison",
          "Post-Training Analytics",
          "Quiz Performance Tracking",
          "Attendance Management",
          "Professional Qualifications"
        ]
      }
    };

    return configs[dashboardType];
  };

  // Enabled dashboard modules. Add the module key here when a new module
  // ships (together with its Layout nav, App.js route and ManageData card —
  // docs/extending.md).
  const ENABLED_MODULES = ['qm', 'ld'];

  const getAvailableDashboards = () => {
    if (!isAuthenticated()) return [];

    const dashboards = [];
    const userModules = user?.modules || [];
    const userPermissions = user?.permissions || [];
    const isSuperAdmin = user?.role === 'super_admin';

    const hasModuleAccess = (mod) =>
      isSuperAdmin || userModules.includes(mod) || userPermissions.some(p => p.startsWith(`${mod}:`));

    for (const mod of ENABLED_MODULES) {
      if (hasModuleAccess(mod)) {
        dashboards.push({ type: mod, ...getDashboardConfig(mod) });
      }
    }

    return dashboards;
  };

  const value = {
    currentDashboard,
    setCurrentDashboard,
    switchDashboard,
    dashboardHistory,
    alerts,
    setAlerts,
    userPreferences,
    setUserPreferences,
    addBookmark,
    removeBookmark,
    getDashboardConfig,
    getAvailableDashboards
  };

  return (
    <UnifiedDashboardContext.Provider value={value}>
      {children}
    </UnifiedDashboardContext.Provider>
  );
};